import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PagoReal } from '../types';
import paymentsService from '../services/payments.service';

const PagoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pago, setPago] = useState<PagoReal | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPago = async () => {
      if (!id) return;
      
      try {
        const data = await paymentsService.getPagoById(parseInt(id));
        setPago(data);
      } catch (error) {
        console.error('Error fetching pago:', error);
        alert('Error al cargar el pago');
        navigate('/pagos');
      } finally {
        setLoading(false);
      }
    };

    fetchPago();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!pago || !window.confirm('¿Estás seguro de que deseas eliminar este pago?')) {
      return;
    }

    try {
      await paymentsService.deletePago(pago.id);
      navigate('/pagos');
    } catch (error) {
      console.error('Error deleting pago:', error);
      alert('Error al eliminar el pago');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!pago) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Pago no encontrado</h3>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/pagos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a pagos
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Pago #{pago.id}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {pago.contrato?.cliente?.nombre} - {pago.contrato?.numeroContrato}
            </p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/pagos/${pago.id}/editar`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Editar
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Eliminar
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información Principal */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Detalles del Pago</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Monto</dt>
                <dd className="mt-1 text-lg font-semibold text-green-600">
                  ${pago.monto.toLocaleString('es-CO')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fecha de Pago</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(pago.fechaPago).toLocaleDateString('es-CO')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Método de Pago</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">{pago.metodoPago}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Referencia</dt>
                <dd className="mt-1 text-sm text-gray-900">{pago.referencia || 'N/A'}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Fecha de Registro</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(pago.createdAt).toLocaleDateString('es-CO')} a las {new Date(pago.createdAt).toLocaleTimeString('es-CO')}
                </dd>
              </div>
            </div>
            
            {pago.observaciones && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">Observaciones</dt>
                <dd className="mt-2 text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                  {pago.observaciones}
                </dd>
              </div>
            )}
          </div>

          {/* Información del Contrato */}
          {pago.contrato && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Información del Contrato</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Número de Contrato</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium">{pago.contrato.numeroContrato}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cliente</dt>
                  <dd className="mt-1 text-sm text-gray-900">{pago.contrato.cliente?.nombre}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Valor Total del Contrato</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-semibold">
                    ${pago.contrato.valorTotal.toLocaleString('es-CO')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Estado del Contrato</dt>
                  <dd className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        pago.contrato.estado === 'vigente'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {pago.contrato.estado}
                    </span>
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Objeto del Contrato</dt>
                  <dd className="mt-1 text-sm text-gray-900">{pago.contrato.objeto}</dd>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Información Adicional</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Registrado por</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {pago.user?.email || 'Sistema'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Última Actualización</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(pago.updatedAt).toLocaleDateString('es-CO')}
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones</h3>
            <div className="space-y-3">
              <Link
                to={`/contratos/${pago.contratoId}`}
                className="block w-full text-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
              >
                Ver Contrato
              </Link>
              <Link
                to={`/clientes/${pago.contrato?.clienteId}`}
                className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Ver Cliente
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagoDetail;